import React, { useEffect, useId, useState } from 'react';
import { X } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import axios from 'axios';
import './ContactSalesModal.css';

const INTERESTS = [
  "I'm interested in Rentid",
  "I'm interested in RentShield Insurance",
  "I'm interested in RentID Enterprise",
  "I'm interested in an API or integration",
  "I'm interested in a partnership",
  'Other sales inquiry',
];

// Accepts bare domains as well as full URLs — prospects type "acme.com" far
// more often than they type the scheme.
const URL_PATTERN = /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(\/\S*)?$/i;

const validationSchema = Yup.object().shape({
  firstName: Yup.string().trim().required('First name is required'),
  lastName: Yup.string().trim().required('Last name is required'),
  email: Yup.string().trim().email('Email is invalid').required('Email is required'),
  phone: Yup.string()
    .required('Phone number is required')
    .min(12, 'Phone number must be at least 10 digits'),
  companyWebsite: Yup.string()
    .trim()
    .required('Company website is required')
    .matches(URL_PATTERN, 'Enter a valid website address'),
  industry: Yup.string().trim().required('Industry is required'),
  linkedinProfile: Yup.string()
    .trim()
    .required('LinkedIn profile is required')
    .matches(URL_PATTERN, 'Enter a valid LinkedIn URL'),
  interests: Yup.string().required('Please select an option'),
  message: Yup.string().trim().required('Please tell us how we can help'),
});

const DEFAULTS = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  companyWebsite: '',
  industry: '',
  linkedinProfile: '',
  interests: '',
  message: '',
};

/**
 * Contact Sales lead form, shown as a modal over the INSHUR landing page.
 * Submits to the same-origin /api/contact-submit function, which emails the
 * team via Resend.
 */
const ContactSalesModal = ({ open, onOpenChange }) => {
  const [status, setStatus] = useState({ state: 'idle', message: '' });
  const fieldId = useId();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    control,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event) => {
      if (event.key === 'Escape') onOpenChange(false);
    };
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [open, onOpenChange]);

  // Reopening after a send should offer a blank form, not the previous
  // submission's success banner.
  useEffect(() => {
    if (!open) {
      setStatus({ state: 'idle', message: '' });
      reset(DEFAULTS);
    }
  }, [open, reset]);

  if (!open) return null;

  const onSubmit = async (data) => {
    setStatus({ state: 'idle', message: '' });

    try {
      const { data: response } = await axios.post('/api/contact-submit', {
        first_name: data.firstName,
        last_name: data.lastName,
        email_address: data.email,
        phone_number: data.phone,
        message: data.message,
        company_website: data.companyWebsite,
        industry: data.industry,
        linkedin_profile: data.linkedinProfile,
        // The endpoint takes a list; this form collects a single choice.
        interests: [data.interests],
        source: 'inshur',
      });

      if (response.success === true) {
        setStatus({
          state: 'success',
          message: "Thanks — we've got your details and will be in touch shortly.",
        });
        reset(DEFAULTS);
        return;
      }

      setStatus({ state: 'error', message: 'Could not send your message. Please try again.' });
    } catch (error) {
      // The function returns its reason under `error`, not `message`.
      const detail = error.response?.data?.error;
      setStatus({
        state: 'error',
        message: detail || 'Could not send your message. Please try again.',
      });
    }
  };

  const describedBy = (name) => (errors[name] ? `${fieldId}-${name}-error` : undefined);

  const fieldError = (name) =>
    errors[name] ? (
      <p className="ilp-csm-error" id={`${fieldId}-${name}-error`}>
        {errors[name].message}
      </p>
    ) : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${fieldId}-title`}
      className="ilp-csm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onOpenChange(false);
      }}
    >
      <div className="ilp-csm-panel">
        <button
          type="button"
          className="ilp-csm-close"
          onClick={() => onOpenChange(false)}
          aria-label="Close form"
        >
          <X className="ilp-csm-close-icon" aria-hidden="true" />
        </button>

        <div className="ilp-csm-head">
          <p className="ilp-csm-eyebrow">Contact Sales</p>
          <h2 className="ilp-csm-title" id={`${fieldId}-title`}>
            Let&rsquo;s talk about your fleet.
          </h2>
          <p className="ilp-csm-sub">
            Tell us a little about your business and the right person will get back to you.
          </p>
        </div>

        <form className="ilp-csm-form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="ilp-csm-grid">
            <div className="ilp-csm-field">
              <label className="ilp-csm-label" htmlFor={`${fieldId}-firstName`}>
                First name<span className="ilp-csm-req" aria-hidden="true">*</span>
              </label>
              <input
                id={`${fieldId}-firstName`}
                type="text"
                className={`ilp-csm-input${errors.firstName ? ' is-invalid' : ''}`}
                placeholder="Jane"
                aria-invalid={errors.firstName ? 'true' : 'false'}
                aria-describedby={describedBy('firstName')}
                {...register('firstName')}
              />
              {fieldError('firstName')}
            </div>

            <div className="ilp-csm-field">
              <label className="ilp-csm-label" htmlFor={`${fieldId}-lastName`}>
                Last name<span className="ilp-csm-req" aria-hidden="true">*</span>
              </label>
              <input
                id={`${fieldId}-lastName`}
                type="text"
                className={`ilp-csm-input${errors.lastName ? ' is-invalid' : ''}`}
                placeholder="Doe"
                aria-invalid={errors.lastName ? 'true' : 'false'}
                aria-describedby={describedBy('lastName')}
                {...register('lastName')}
              />
              {fieldError('lastName')}
            </div>

            <div className="ilp-csm-field">
              <label className="ilp-csm-label" htmlFor={`${fieldId}-email`}>
                Email<span className="ilp-csm-req" aria-hidden="true">*</span>
              </label>
              <input
                id={`${fieldId}-email`}
                type="email"
                className={`ilp-csm-input${errors.email ? ' is-invalid' : ''}`}
                placeholder="jane@company.com"
                aria-invalid={errors.email ? 'true' : 'false'}
                aria-describedby={describedBy('email')}
                {...register('email')}
              />
              {fieldError('email')}
            </div>

            <div className="ilp-csm-field">
              <label className="ilp-csm-label" htmlFor={`${fieldId}-phone`}>
                Phone<span className="ilp-csm-req" aria-hidden="true">*</span>
              </label>
              <Controller
                name="phone"
                control={control}
                disabled={isSubmitting}
                render={({ field }) => (
                  // Configured to match the phone field on /general-question.
                  <PhoneInput
                    {...field}
                    id={`${fieldId}-phone`}
                    disabled={isSubmitting}
                    limitMaxLength
                    defaultCountry="US"
                    placeholder="XXX-XXX-XXX"
                    className={`ilp-csm-phone${errors.phone ? ' is-invalid' : ''}`}
                    value={field.value || ''}
                    onChange={(value) => field.onChange(value)}
                  />
                )}
              />
              {fieldError('phone')}
            </div>

            <div className="ilp-csm-field">
              <label className="ilp-csm-label" htmlFor={`${fieldId}-companyWebsite`}>
                Company website<span className="ilp-csm-req" aria-hidden="true">*</span>
              </label>
              <input
                id={`${fieldId}-companyWebsite`}
                type="text"
                inputMode="url"
                className={`ilp-csm-input${errors.companyWebsite ? ' is-invalid' : ''}`}
                placeholder="company.com"
                aria-invalid={errors.companyWebsite ? 'true' : 'false'}
                aria-describedby={describedBy('companyWebsite')}
                {...register('companyWebsite')}
              />
              {fieldError('companyWebsite')}
            </div>

            <div className="ilp-csm-field">
              <label className="ilp-csm-label" htmlFor={`${fieldId}-industry`}>
                Industry<span className="ilp-csm-req" aria-hidden="true">*</span>
              </label>
              <input
                id={`${fieldId}-industry`}
                type="text"
                className={`ilp-csm-input${errors.industry ? ' is-invalid' : ''}`}
                aria-invalid={errors.industry ? 'true' : 'false'}
                aria-describedby={describedBy('industry')}
                {...register('industry')}
              />
              {fieldError('industry')}
            </div>

            <div className="ilp-csm-field ilp-csm-field--full">
              <label className="ilp-csm-label" htmlFor={`${fieldId}-linkedinProfile`}>
                LinkedIn profile<span className="ilp-csm-req" aria-hidden="true">*</span>
              </label>
              <input
                id={`${fieldId}-linkedinProfile`}
                type="text"
                inputMode="url"
                className={`ilp-csm-input${errors.linkedinProfile ? ' is-invalid' : ''}`}
                placeholder="linkedin.com/in/janedoe"
                aria-invalid={errors.linkedinProfile ? 'true' : 'false'}
                aria-describedby={describedBy('linkedinProfile')}
                {...register('linkedinProfile')}
              />
              {fieldError('linkedinProfile')}
            </div>
          </div>

          <div className="ilp-csm-field">
            <label className="ilp-csm-label" htmlFor={`${fieldId}-interests`}>
              What are you interested in?<span className="ilp-csm-req" aria-hidden="true">*</span>
            </label>
            <select
              id={`${fieldId}-interests`}
              className={`ilp-csm-input ilp-csm-select${errors.interests ? ' is-invalid' : ''}`}
              aria-invalid={errors.interests ? 'true' : 'false'}
              aria-describedby={describedBy('interests')}
              {...register('interests')}
            >
              <option value="">Select an option</option>
              {INTERESTS.map((interest) => (
                <option key={interest} value={interest}>
                  {interest}
                </option>
              ))}
            </select>
            {fieldError('interests')}
          </div>

          <div className="ilp-csm-field">
            <label className="ilp-csm-label" htmlFor={`${fieldId}-message`}>
              Tell us how we can help you<span className="ilp-csm-req" aria-hidden="true">*</span>
            </label>
            <textarea
              id={`${fieldId}-message`}
              rows={4}
              className={`ilp-csm-input ilp-csm-textarea${errors.message ? ' is-invalid' : ''}`}
              aria-invalid={errors.message ? 'true' : 'false'}
              aria-describedby={describedBy('message')}
              {...register('message')}
            />
            {fieldError('message')}
          </div>

          {status.state !== 'idle' && (
            <p
              className={`ilp-csm-status ilp-csm-status--${status.state}`}
              role={status.state === 'error' ? 'alert' : 'status'}
            >
              {status.message}
            </p>
          )}

          <button type="submit" className="ilp-csm-submit" disabled={isSubmitting}>
            {isSubmitting ? 'Sending…' : 'Submit'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContactSalesModal;
